import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_TAMS } from '@bloomays-lib/adapter.api-talent';
import type { ActiveTam, GetActiveTamsQueryData } from '../types/primes';
import { buildTamDisplayName } from '../utils/clevelPrimesDashboard.utils';

const EMPTY_ACTIVE_TAMS: ActiveTam[] = [];

const normalizeTamEmail = (email: string): string => email.trim().toLowerCase();

export type UseActiveTamsOptions = {
  fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
  onError?: (error: Error) => void;
};

export const useActiveTams = ({ fetchPolicy = 'cache-first', onError }: UseActiveTamsOptions = {}) => {
  const { loading, error, data, refetch } = useQuery<GetActiveTamsQueryData>(GET_ACTIVE_TAMS, {
    context: { clientName: 'api.talents' },
    fetchPolicy,
    onError,
  });

  const activeTams = data?.getActiveTams ?? EMPTY_ACTIVE_TAMS;

  const tamByEmail = useMemo(() => new Map(activeTams.map((tam) => [normalizeTamEmail(tam.email), tam])), [activeTams]);

  const getTamByEmail = useCallback(
    (email: string): ActiveTam | undefined => tamByEmail.get(normalizeTamEmail(email)),
    [tamByEmail],
  );

  const getTamDisplayName = useCallback(
    (email: string): string => {
      const tam = getTamByEmail(email);
      return tam ? buildTamDisplayName(tam) : email.trim();
    },
    [getTamByEmail],
  );

  return {
    activeTams,
    loading,
    error,
    getTamByEmail,
    getTamDisplayName,
    refetch,
  };
};
